import { useCallback, useMemo, useState } from 'react';
import type { TWidget } from 'types/hubDashboard.type';
import { Form, Modal } from '@timeedit/ui-components/antd';
import useForm from '@timeedit/ui-components/antd/form/hooks/useForm';
import type { Store } from '@timeedit/ui-components/antd/form/interface';
import WidgetFormFields from './WidgetModalField.component';
import './WidgetModal.scss';

interface ModalProps {
  widget?: TWidget;
  onSubmit: (newWidget: TWidget) => Promise<void> | void;
}

const WidgetModal = ({ onSubmit, widget }: ModalProps) => {
  const valuesArrayParams = useCallback(
    (parameters: Record<string, string>) => {
      const newParameters: Record<string, any> = {};
      Object.entries(parameters).forEach(([key, value]) => {
        if (Array.isArray(parameters[key])) {
          parameters[key].forEach((props, index) => {
            Object.entries(props).forEach(([propKey, value]) => {
              newParameters[`array-${index}-${key}-${propKey}`] =
                value as string;
            });
          });
          return;
        }
        newParameters[key] = value;
      });
      return newParameters;
    },
    []
  );

  const initialValues = useMemo<Store | undefined>(() => {
    if (!widget) return;

    const { id, parameters, ...rest } = widget;
    return {
      ...rest,
      ...valuesArrayParams(parameters),
    };
  }, []);

  const parseArrayParams = useCallback((parameters: Record<string, string>) => {
    const newParameters: Record<string, any> = {};
    Object.entries(parameters).forEach(([key, value]) => {
      if (key.startsWith('array-')) {
        const [, index, name, prop] = key.split('-');
        if (!newParameters[name]) {
          newParameters[name] = [];
        }
        if (!newParameters[name][Number(index)]) {
          newParameters[name][Number(index)] = {};
        }
        newParameters[name][Number(index)][prop] = value;
        return;
      }
      newParameters[key] = value;
    });

    return newParameters;
  }, []);

  const [form] = useForm();
  return (
    <Form
      form={form}
      name="add-widget-dashboard"
      onFinish={({ type, ...initialParameters }) => {
        const parameters = parseArrayParams(initialParameters);
        onSubmit({ type, parameters } as TWidget);
      }}
      className="widget-modal-form"
      initialValues={initialValues}
    >
      <WidgetFormFields initialValues={initialValues} />
    </Form>
  );
};

interface IUseWidgetModal {
  widget?: TWidget;
  onSubmit: (payload: { index?: number; widget: TWidget }) => void;
}

export default function useEditWidgetModal({
  onSubmit,
}: Omit<IUseWidgetModal, 'widget'>) {
  const [data, setWidget] = useState<{ index: number; widget: TWidget }>();
  const [modalType, setModalType] = useState<'edit' | 'add'>('add');

  const [visible, setVisible] = useState(false);
  const setOpen = useCallback(() => setVisible(true), []);
  const setClose = useCallback(() => setVisible(false), []);

  const widget = useMemo(() => {
    if (!data) return;
    return data.widget;
  }, [data]);

  const RenderModal = () => (
    <Modal
      title={`${modalType === 'edit' ? 'Edit' : 'Add'} widget`}
      open={visible}
      onCancel={setClose}
      footer={null}
    >
      <WidgetModal
        onSubmit={(newWidget) => {
          onSubmit({ index: data?.index, widget: newWidget });
          setClose();
        }}
        widget={widget}
      />
    </Modal>
  );

  return { setOpen, setModalType, setClose, setWidget, RenderModal };
}
